import styles from "../../styles/shared/other-coins-signal/SelectAssets.module.css";
import { useDispatch, useSelector } from "react-redux";
import { Checkbox, Spin } from "antd";
import React from "react";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { IoIosClose } from "react-icons/io";
import useUserData from "../../hooks/useUserData";
import {
  createWatchList,
  deleteWatchlist,
  modifyWatchlist,
} from "../../redux/new-insight/actions";
import {
  WEEKLY_CORE_ID,
  WEEKLY_TACTICAL_ID,
  coinKey,
  parseWatchlistCoins,
  sameCoin,
  weeklyLabelForCoin,
} from "./watchlistCoins";

const findClosestWatchlist = (asset, watchlistsData) => {
  for (const key in watchlistsData) {
    if (watchlistsData[key].includes(asset)) {
      return key;
    }
  }
  return "";
};

const matchesSearch = (asset, searchedCoin) =>
  asset?.toLowerCase()?.includes((searchedCoin || "").toLowerCase());

const coinsOnExchange = (symbols, exchangeAssets) => {
  const wanted = new Set((symbols || []).map(coinKey));
  return (exchangeAssets || []).filter((asset) => wanted.has(coinKey(asset)));
};

function SelectAssetsOptions({
  selectedAssets,
  setSelectedAssets,
  assets,
  personalCoins,
  watchlistBusy,
  onAdd,
  onRemoveSelected,
}) {
  const selectedBases = selectedAssets?.map((asset) => coinKey(asset)) || [];
  const selectedInPersonal = selectedBases.filter((coin) =>
    personalCoins.some((saved) => coinKey(saved) === coin),
  );
  const canAdd =
    selectedAssets?.length > 0 &&
    selectedInPersonal.length < selectedBases.length;
  const canRemove = selectedInPersonal.length > 0;

  const handleSelection = () => {
    if (selectedAssets?.length) {
      setSelectedAssets([]);
    } else {
      setSelectedAssets(assets);
    }
  };

  return (
    <div className={styles.selectOptions}>
      <span onClick={handleSelection} className={styles.selectAllBtn}>
        {selectedAssets?.length ? "Deselect All" : "Select All"}
      </span>
      {(canAdd || canRemove) && (
        <div className={styles.watchlistActions}>
          {watchlistBusy ? (
            <span className={styles.addToWatchlistBtn}>
              <Spin size="small" /> Updating watchlist...
            </span>
          ) : (
            <>
              {canRemove && (
                <span
                  className={styles.removeFromWatchlistBtn}
                  onClick={onRemoveSelected}
                >
                  Remove from personal watchlist
                </span>
              )}
              {canAdd && (
                <span className={styles.addToWatchlistBtn} onClick={onAdd}>
                  Add to personal watchlist
                </span>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function SelectAssets({
  selectedExchange,
  changeSelectedAssets,
  selectedAssets,
  selectedWatchlist,
  searchedCoin = "",
  weeklyPortfolio = { core: [], tactical: [] },
}) {
  const {
    exchangeState: { allExchangesWithCoins, coinCategories },
    newInsightState: {
      watchlists: personalWatchlists,
      createWatchlistStatus,
      modifyWatchListStatus,
      deleteWatchListStatus,
    },
  } = useSelector((state) => state);

  const dispatch = useDispatch();
  const { username, email } = useUserData();

  const watchlistBusy =
    createWatchlistStatus === "loading" ||
    modifyWatchListStatus === "loading" ||
    deleteWatchListStatus === "loading";

  const currentWatchlist = personalWatchlists?.[0];
  const personalCoins = parseWatchlistCoins(currentWatchlist?.list_of_coins);

  const exchangeAssets =
    allExchangesWithCoins
      ?.find(
        (item) =>
          item?.exchange?.toLowerCase() === selectedExchange?.toLowerCase(),
      )
      ?.coins?.map((coinData) => coinData?.Coin)
      ?.filter((coin) => matchesSearch(coin, searchedCoin)) || [];

  const personalIndex = selectedWatchlist?.startsWith("personal-")
    ? Number(selectedWatchlist.split("-")[1])
    : 0;
  const personalListCoins = parseWatchlistCoins(
    personalWatchlists?.[personalIndex]?.list_of_coins,
  );

  const watchlistAssets = coinCategories?.[selectedWatchlist];

  const assets =
    selectedWatchlist === "all"
      ? exchangeAssets
      : selectedWatchlist === WEEKLY_CORE_ID
        ? coinsOnExchange(weeklyPortfolio?.core, exchangeAssets)
        : selectedWatchlist === WEEKLY_TACTICAL_ID
          ? coinsOnExchange(weeklyPortfolio?.tactical, exchangeAssets)
          : selectedWatchlist?.startsWith("personal-")
            ? coinsOnExchange(personalListCoins, exchangeAssets)
            : watchlistAssets
                ?.filter((asset) => exchangeAssets?.includes(asset))
                ?.filter((asset) => matchesSearch(asset, searchedCoin));

  const persistCoins = (watchlist, nextCoins) => {
    if (watchlistBusy || !watchlist) return;
    const createdDate = watchlist?.createdDate;
    if (!nextCoins.length) {
      dispatch(deleteWatchlist(username, email, createdDate));
      return;
    }
    dispatch(modifyWatchlist(username, email, createdDate, nextCoins));
  };

  const removeCoins = (watchlist, coinsToRemove) => {
    const saved = parseWatchlistCoins(watchlist?.list_of_coins);
    const removeKeys = new Set((coinsToRemove || []).map(coinKey));
    const remaining = saved.filter((coin) => !removeKeys.has(coinKey(coin)));
    persistCoins(watchlist, remaining);
  };

  const addToWatchlist = () => {
    if (watchlistBusy) return;
    const selectedCoins = [
      ...new Set((selectedAssets || []).map((asset) => coinKey(asset))),
    ];
    if (personalCoins.length > 0) {
      const newCoins = selectedCoins.filter(
        (coin) => !personalCoins.some((saved) => sameCoin(saved, coin)),
      );
      if (!newCoins.length) return;
      persistCoins(currentWatchlist, [...personalCoins, ...newCoins]);
      return;
    }
    dispatch(createWatchList(username, email, selectedCoins));
  };

  const viewingPersonal = selectedWatchlist?.startsWith("personal-");

  return (
    <div>
      <SelectAssetsOptions
        selectedAssets={selectedAssets}
        setSelectedAssets={changeSelectedAssets}
        assets={assets}
        personalCoins={personalCoins}
        watchlistBusy={watchlistBusy}
        onAdd={addToWatchlist}
        onRemoveSelected={() => removeCoins(currentWatchlist, selectedAssets)}
      />

      <ul className={styles.assetsList}>
        {assets?.length ? (
          assets?.map((asset) => (
            <li key={asset} className={styles.assetItem}>
              <Checkbox
                key={asset}
                checked={selectedAssets?.includes(asset)}
                onChange={(e) => {
                  if (!e.target.checked) {
                    changeSelectedAssets((prevAssets) =>
                      prevAssets?.filter((assetItem) => assetItem !== asset),
                    );
                  } else {
                    changeSelectedAssets((prevAssets) => [
                      ...prevAssets,
                      asset,
                    ]);
                  }
                }}
              >
                <LazyLoadImage
                  src={`https://cryptocrispypro.s3.ap-southeast-2.amazonaws.com/symbol_icons/${asset?.replace("/USDT", "")}.png`}
                  alt={asset}
                  className={styles.symbolImage}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/placeholders/asset-logo.svg";
                  }}
                  width="20px"
                  height="20px"
                  effect="blur"
                  style={{ borderRadius: "50%" }}
                />
                {`${asset}/USDT`}
              </Checkbox>
              <span className={styles.assetMeta}>
                {selectedWatchlist === "all" && (
                  <span className={styles.categoryName}>
                    {weeklyLabelForCoin(asset, weeklyPortfolio) ||
                      findClosestWatchlist(asset, coinCategories) ||
                      (personalCoins.some((coin) => sameCoin(coin, asset)) &&
                        "Personal")}
                  </span>
                )}
                {viewingPersonal && (
                  <button
                    type="button"
                    className={styles.removeCoinBtn}
                    aria-label={`Remove ${asset} from personal watchlist`}
                    disabled={watchlistBusy}
                    onClick={() =>
                      removeCoins(personalWatchlists?.[personalIndex], [asset])
                    }
                  >
                    <IoIosClose />
                  </button>
                )}
              </span>
            </li>
          ))
        ) : (
          <li style={{ textAlign: "center" }}>No matched coins...</li>
        )}
      </ul>
    </div>
  );
}

export default SelectAssets;
